<template>
  <lb-dialog
    :value="value"
    :heading="heading"
    width="880"
    @input="$emit('input', $event)"
  >
    <template v-slot:body>
      <div v-if="showStepperHeader" class="fc-notification-stepper" data-testid="notification-stepper__header">
        <span
          class="fc-notification-stepper__step"
          :class="{
            'fc-notification-stepper__step--done': step > 1,
            'fc-notification-stepper__step--active': step === 1,
          }"
        >
          <span class="fc-notification-stepper__n">
            <v-icon v-if="step > 1" x-small>mdi-check</v-icon>
            <template v-else>1</template>
          </span>
          Select type
        </span>
        <span
          class="fc-notification-stepper__line"
          :class="{ 'fc-notification-stepper__line--done': step > 1 }"
        />
        <span
          class="fc-notification-stepper__step"
          :class="{ 'fc-notification-stepper__step--active': step === 2 }"
        >
          <span class="fc-notification-stepper__n">2</span>
          Configure
        </span>
      </div>
      <NotificationTypeCardGrid
        v-if="step === 1"
        :types="cards"
        :value="selectedFeature"
        @input="selectType"
      />
      <template v-else-if="value && formComponent">
        <v-alert
          v-if="serverErrorMessages.length"
          type="error"
          dense
          text
          class="fc-notification-form-errors"
          data-testid="notification-stepper__server-errors"
        >
          <ul v-if="serverErrorMessages.length > 1" class="fc-notification-form-errors__list">
            <li v-for="(msg, index) in serverErrorMessages" :key="index">{{ msg }}</li>
          </ul>
          <span v-else>{{ serverErrorMessages[0] }}</span>
        </v-alert>
        <component
          :is="formComponent"
          :key="formKey"
          ref="form"
          :value="notification || {}"
          :locations="locations"
          :server-errors="serverErrors"
        />
      </template>
    </template>
    <template #actions>
      <span v-if="showScheduleNotice" class="fc-notification-notice">{{ scheduleNotice }}</span>
      <span v-else-if="step === 1 && selectedCardLabel" class="fc-notification-selected">
        {{ selectedCardLabel }} selected
      </span>
      <v-btn
        v-if="step === 1"
        class="secondaryBtn text-none"
        data-testid="notification-stepper__cancel"
        @click="$emit('input', false)"
      >
        Cancel
      </v-btn>
      <v-btn
        v-else-if="showStepperHeader"
        class="secondaryBtn text-none fc-notification-stepper__back"
        data-testid="notification-stepper__back"
        @click="back"
      >
        <v-icon left small class="fc-notification-stepper__back-icon">mdi-chevron-left</v-icon>
        Back
      </v-btn>
      <v-btn
        v-if="step === 1"
        color="primary"
        class="primary text-none"
        :disabled="!selectedFeature"
        data-testid="notification-stepper__next"
        @click="next"
      >
        Next
      </v-btn>
      <v-btn
        v-else
        color="primary"
        class="primary text-none"
        :loading="saving"
        data-testid="notification-stepper__finish"
        @click="finish"
      >
        Finish
      </v-btn>
    </template>
  </lb-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { HTTP_BAD_REQUEST } from '@/utils/http';
import {
  EMAIL_NOTIFICATION,
  NOTIFICATION_FEATURE_KIND,
  NOTIFICATION_FEATURE_LABEL,
  NOTIFICATION_KIND,
  SCHEDULE_CHANGE_NOTICE,
} from '../../../../utils/constants';
import { getApiErrorMessage } from '../../../../utils/apiErrorUtils';
import { normalizeNotificationServerErrors } from '../../../../utils/notificationFormErrors';
import NotificationTypeCardGrid from './NotificationTypeCardGrid.vue';
import TaskStatusReminderForm from '../forms/TaskStatusReminderForm.vue';
import UpcomingDueForm from '../forms/UpcomingDueForm.vue';
import ReconChangeForm from '../forms/ReconChangeForm.vue';
import TaskRejectedForm from '../forms/TaskRejectedForm.vue';
import DashboardTemplateForm from '../forms/DashboardTemplateForm.vue';
import EscalationTemplateForm from '../forms/EscalationTemplateForm.vue';

const FORMS = {
  taskStatusReminder: 'TaskStatusReminderForm',
  overdue: 'TaskStatusReminderForm',
  dueToday: 'TaskStatusReminderForm',
  upcomingDueByLocation: 'UpcomingDueForm',
  reconStatusChange: 'ReconChangeForm',
  taskRejected: 'TaskRejectedForm',
  dashboard: 'DashboardTemplateForm',
  taskOverDueNotification: 'EscalationTemplateForm',
};

export default {
  name: 'NotificationStepperDialog',
  components: {
    NotificationTypeCardGrid,
    TaskStatusReminderForm,
    UpcomingDueForm,
    ReconChangeForm,
    TaskRejectedForm,
    DashboardTemplateForm,
    EscalationTemplateForm,
  },
  props: {
    value: { type: Boolean, default: false },
    notification: { type: Object, default: null },
    initialFeature: { type: String, default: '' },
  },
  data() {
    return {
      step: 1,
      selectedFeature: '',
      serverErrors: {},
      serverErrorMessages: [],
      saving: false,
    };
  },
  computed: {
    ...mapGetters('emailNotifications', ['notificationTypeCards', 'fetchLocations']),
    locations() {
      return this.fetchLocations || [];
    },
    cards() {
      return this.notificationTypeCards || [];
    },
    isTemplate() {
      return NOTIFICATION_FEATURE_KIND[this.selectedFeature] === NOTIFICATION_KIND.TEMPLATE;
    },
    showStepperHeader() {
      return !this.isTemplate && !this.notification;
    },
    formComponent() {
      return FORMS[this.selectedFeature] || null;
    },
    formKey() {
      return `${this.selectedFeature}:${this.notification?._id || 'new'}`;
    },
    heading() {
      if (this.notification) return `Edit ${NOTIFICATION_FEATURE_LABEL[this.selectedFeature] || 'notification'}`;
      if (this.selectedFeature === 'taskOverDueNotification') return 'New escalation template';
      if (this.selectedFeature === 'dashboard') return 'New dashboard digest template';
      return 'New notification';
    },
    showScheduleNotice() {
      return this.step === 2 && NOTIFICATION_FEATURE_KIND[this.selectedFeature] === NOTIFICATION_KIND.SCHEDULE;
    },
    scheduleNotice() {
      return SCHEDULE_CHANGE_NOTICE;
    },
    selectedCardLabel() {
      const card = this.cards.find((row) => row.feature === this.selectedFeature);
      return card ? card.label : '';
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (!open) return;
        this.resetForOpen();
      },
    },
    notification() {
      if (this.value) this.resetForOpen();
    },
  },
  methods: {
    ...mapActions('emailNotifications', ['saveNotification']),
    resetForOpen() {
      this.selectedFeature = this.initialFeature || this.notification?.feature || '';
      this.step = this.selectedFeature ? 2 : 1;
      this.serverErrors = {};
      this.serverErrorMessages = [];
    },
    selectType(feature) {
      this.selectedFeature = feature;
    },
    next() {
      if (this.selectedFeature) this.step = 2;
    },
    back() {
      this.step = 1;
    },
    async finish() {
      if (!this.$refs.form || !this.$refs.form.toPayload) return;
      if (typeof this.$refs.form.validate === 'function' && !this.$refs.form.validate()) return;
      this.saving = true;
      this.serverErrors = {};
      this.serverErrorMessages = [];
      try {
        await this.saveNotification({
          type: this.selectedFeature,
          payload: this.$refs.form.toPayload(),
        });
        this.$emit('saved');
        this.$emit('input', false);
      } catch (error) {
        if (error?.response?.status === HTTP_BAD_REQUEST) {
          const normalized = normalizeNotificationServerErrors(
            error?.response?.data?.data,
            getApiErrorMessage(error, EMAIL_NOTIFICATION.MESSAGES.FAILED_UPDATE)
          );
          this.serverErrors = normalized.byField;
          this.serverErrorMessages = normalized.messages;
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import '../../../../components/Notifications/notificationLayout.scss';
</style>
